import { TIMEZONES } from '../../../views/timezones';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ContractsService } from 'src/app/services/contracts/contracts.service';
import { UserService } from 'src/app/services/user/user.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { APPT_TYPES, INDUSTRIES, TIME_ZONES, CONTRACT_TYPES, PERIODICITY, SOURCES } from 'src/app/shared/constants';
import Swal from 'sweetalert2';

@Component({
	selector: 'app-contract-wizard',
	templateUrl: './contract-wizard.component.html',
	styleUrls: ['./contract-wizard.component.scss'],
})
export class ContractWizardComponent implements OnInit {
	@Input() client_data: any;

	basicContractInfo: FormGroup;
	scheduleInfo: FormGroup;
	additionalInfo: FormGroup;
	loading = false;

	constructor(public activeModal: NgbActiveModal, private fb: FormBuilder, public userSrv: UserService, public utils: UtilsService, public contractSrv: ContractsService) {
		// Step 1: Basic Contract Info
		this.basicContractInfo = this.fb.group({
			userid: ['', Validators.required],
			alias: [''],
			isActive: [1, Validators.required],
			periodicity: ['', Validators.required],
			industry: ['', Validators.required],
			contract_type: ['', Validators.required],
			reserve_amount: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
			cost_hour: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
			cost_lead: ['', [Validators.required, Validators.pattern('^[0-9]*.?[0-9]*$')]],
			total_leads_period: [0, [Validators.required, Validators.pattern('^[0-9]*$')]],
			source: ['T3direct Marketing', Validators.required],
			pricing_description: [''],
			pricing_value: [0],
		});

		this.basicContractInfo.get('source')?.markAsTouched();

		// Step 2: Schedule Information
		this.scheduleInfo = this.fb.group({
			start_date: ['', Validators.required],
			end_date: [''],
		});

		// Step 3: Additional Information
		this.additionalInfo = this.fb.group({
			notes: [''],
			date_creation: [new Date().toISOString()],
		});
	}

	contract = {
		userid: 0,
		isActive: 1,
		periodicity: '',
		timezone: '',
		industry: 0,
		appt_types: '',
		contract_type: 1,
		start_date: '',
		end_date: '',
		notes: '',
		cost_lead: 0,
		cost_hour: 0,
		reserve_amount: 0,
		date_creation: new Date().toISOString(),
		pricing_description: '',
		pricing_value: 0,
		alias: '',
		source: 'T3direct Marketing',
	};

	timezones = TIME_ZONES;
	industries = INDUSTRIES;
	appt_types = APPT_TYPES;
	contract_types = CONTRACT_TYPES;
	periods = PERIODICITY;
	sources = SOURCES;

	// Available users (these should be populated from a service in a real implementation)
	users = [];

	ngOnInit() {
		if (this.client_data) {
			this.basicContractInfo.patchValue({ userid: this.client_data.id, alias: this.client_data.alias });
			this.basicContractInfo.get('userid')?.markAsTouched();
		}
		this.getUsers();
	}

	step = 1;
	modalTitles = ['Basic Contract Information', 'Schedule Information', 'Notes', 'Contract Summary'];

	next() {
		if (this.step === 1 && !this.basicContractInfo.valid) {
			Object.keys(this.basicContractInfo.controls).forEach((key) => {
				const control = this.basicContractInfo.get(key);
				control?.markAsTouched();
			});
			return;
		}

		if (this.step === 2 && !this.scheduleInfo.valid) {
			Object.keys(this.scheduleInfo.controls).forEach((key) => {
				const control = this.scheduleInfo.get(key);
				control?.markAsTouched();
			});
			return;
		}

		if (this.step === 3 && !this.additionalInfo.valid) {
			Object.keys(this.additionalInfo.controls).forEach((key) => {
				const control = this.additionalInfo.get(key);
				control?.markAsTouched();
			});
			return;
		}

		// Sync values with contract object based on current step
		if (this.step === 1) {
			const formValues = this.basicContractInfo.value;
			Object.keys(formValues).forEach((key) => {
				this.contract[key] = formValues[key];
			});
		} else if (this.step === 2) {
			const formValues = this.scheduleInfo.value;
			Object.keys(formValues).forEach((key) => {
				this.contract[key] = formValues[key];
			});
		} else if (this.step === 3) {
			const formValues = this.additionalInfo.value;
			Object.keys(formValues).forEach((key) => {
				this.contract[key] = formValues[key];
			});
		}

		if (this.step < 4) {
			this.step++;
		}
	}

	prev() {
		if (this.step > 1) {
			this.step--;
		}
	}

	getUsers() {
		this.userSrv.getClientUsersSelect().subscribe((data: any) => {
			this.users = data.body;
		});
	}

	validateField(formGroup: FormGroup, fieldName: string): boolean {
		const field = formGroup.get(fieldName);
		return field ? field.valid && field.touched : false;
	}

	validateStep4() {
		if (this.step === 4 && !this.additionalInfo.valid) {
			Object.keys(this.additionalInfo.controls).forEach((key) => {
				const control = this.additionalInfo.get(key);
				control?.markAsTouched();
			});
			return;
		}

		if (this.step === 4) {
			const formValues = this.additionalInfo.value;
			Object.keys(formValues).forEach((key) => {
				this.contract[key] = formValues[key];
			});

			this.saveContract();
		}
	}

	saveContract() {
		if (this.basicContractInfo.valid && this.scheduleInfo.valid && this.additionalInfo.valid) {
			// Replace this with an actual service call to save the contract
			setTimeout(() => {
				Swal.fire({
					title: 'Success',
					icon: 'success',
					text: 'Contract created successfully',
					showCancelButton: false,
					confirmButtonText: 'Ok',
				});
				this.activeModal.dismiss();
			}, 1000);
		} else {
			Swal.fire({
				title: 'Error',
				icon: 'error',
				text: 'Please validate all required fields in all steps.',
				showCancelButton: false,
				confirmButtonText: 'Ok',
			});
		}
	}

	toggleReserve(value: number) {
		this.basicContractInfo.patchValue({ isReserve: value });
	}

	toggleActive(value: number) {
		this.basicContractInfo.patchValue({ isActive: value });
	}

	// Track selected appointment types
	selectedApptTypes: number[] = [];

	updateSelectedApptTypes(typeId: number) {
		// Check if the appointment type is already selected
		const index = this.selectedApptTypes.indexOf(typeId);

		// Toggle selection: add if not present, remove if already selected
		if (index === -1) {
			this.selectedApptTypes.push(typeId);
		} else {
			this.selectedApptTypes.splice(index, 1);
		}

		// Create a comma-separated string of selected appointment types
		const commaSeparatedTypes = this.selectedApptTypes.join(',');

		// Update the form control value
		this.basicContractInfo.patchValue({
			appt_types: commaSeparatedTypes,
		});

		// Mark as touched to trigger validation
		this.basicContractInfo.get('appt_types')?.markAsTouched();
	}

	updateSelectedContractType(type) {
		switch (type) {
			case 1:
				this.basicContractInfo.get('cost_lead')?.enable();
				this.basicContractInfo.get('reserve_amount')?.disable();
				this.basicContractInfo.get('cost_hour')?.disable();
				this.basicContractInfo.get('pricing_description')?.setValue('Lead Cost');
				break;
			case 2:
				this.basicContractInfo.get('cost_lead')?.disable();
				this.basicContractInfo.get('reserve_amount')?.enable();
				this.basicContractInfo.get('cost_hour')?.disable();
				this.basicContractInfo.get('pricing_description')?.setValue('Lead Cost');
				break;
			case 3:
				this.basicContractInfo.get('cost_lead')?.disable();
				this.basicContractInfo.get('reserve_amount')?.disable();
				this.basicContractInfo.get('cost_hour')?.enable();
				this.basicContractInfo.get('pricing_description')?.setValue('Hour Cost');

				break;

			default:
				break;
		}
	}

	// Helper methods for Summary display

	getUserFullName(userId: any): string {
		if (!userId) return 'Not selected';
		const user = this.users.find(u => u.id == userId);
		return user ? `${user.first} ${user.last}` : 'Unknown user';
	}

	getPeriodLabel(periodValue: string): string {
		if (!periodValue) return 'Not selected';
		const period = this.periods.find(p => p.value === periodValue);
		return period ? period.label : 'Unknown period';
	}

	getSourceLabel (sourceValue: string): string {
		if (!sourceValue) return 'Not selected';
		return this.contract.source ? this.contract.source : 'Unknown source';
	}

	getTimezoneName(timezoneCode: string): string {
		if (!timezoneCode) return 'Not selected';
		const timezone = this.timezones.find(tz => tz.shortName === timezoneCode);
		return timezone ? timezone.name : 'Unknown timezone';
	}

	getContractTypeName(typeId: any): string {
		if (!typeId) return 'Not selected';
		const contractType = this.contract_types.find(ct => ct.id == typeId);
		return contractType ? contractType.name : 'Unknown contract type';
	}

	getApptTypesNames(apptTypesString: string): string {
		if (!apptTypesString) return 'None selected';

		const typeIds = apptTypesString.split(',').map(id => parseInt(id.trim()));
		if (typeIds.length === 0) return 'None selected';

		const typeNames = typeIds.map(id => {
			const type = this.appt_types.find(at => at.id === id);
			return type ? type.description : `Unknown (${id})`;
		});

		return typeNames.join(', ');
	}

	createContract() {

		switch(this.contract.contract_type) {
			case 1:
				this.contract.pricing_description = 'Lead Cost';
				this.contract.pricing_value = this.basicContractInfo.get('cost_lead')?.value;
				break;
			case 2:
				this.contract.pricing_description = 'Lead Cost';
				this.contract.pricing_value = 0;
				break;
			case 3:
				this.contract.pricing_description = 'Hour Cost';
				this.contract.pricing_value = this.basicContractInfo.get('cost_hour')?.value;
				break;
		}

		if (this.basicContractInfo.valid && this.scheduleInfo.valid && this.additionalInfo.valid) {
			this.loading = true;
			this.contractSrv.createContract(this.contract).subscribe((data: any) => {
				this.loading = false;

				let dataForCampaign = data.body;
				dataForCampaign.user_id = this.contract.userid;
				dataForCampaign.alias = this.contract.alias;
				dataForCampaign.contract_type = this.contract.contract_type;
				dataForCampaign.industry = this.contract.industry;
				dataForCampaign.pricing_description = this.contract.pricing_description;
				dataForCampaign.pricing_value = this.contract.pricing_value;

				this.activeModal.close(dataForCampaign);
			}, err => {
				console.error(err);
				Swal.fire({
					title: 'Error',
					icon: 'error',
					text: err.body.error,
					showCancelButton: false,
					confirmButtonText: 'Ok',
				});
			});
		} else {
			this.loading = true;
			Swal.fire({
				title: 'Error',
				icon: 'error',
				text: 'Please validate all required fields in all steps.',
				showCancelButton: false,
				confirmButtonText: 'Ok',
			});
		}


	}

	selectUser(){
		const user_id = this.basicContractInfo.get('userid')?.value;
		const matchedUser = this.users.find((user: any) => user.id == user_id);
		if (matchedUser) {
			this.basicContractInfo.get('alias')?.setValue(matchedUser.alias);
		}
	}

}
